import { FieldModel, FieldsAPI, MpxResponseError, UserId } from '@cibo/core'
import {
  GeometryEditor,
  GeometryEditorContext,
  LayerProvider,
  useGeometryEditorStateContext,
  useIPGeolocation,
} from '@cibo/mapgl'
import { useWindowTitle } from '@cibo/profile'
import { LandThumbnail, LandThumbnailSkin, LineLimitTypography, US_CENTER } from '@cibo/ui'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { FeatureCollection, featureCollection as featureCollectionCreator, union } from '@turf/turf'
import { AxiosError } from 'axios'
import { Position } from 'geojson'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fieldSizeValidation, useCreateField } from '../../'
import { FieldEditorForm } from '../../components/FieldEditorForm'

export interface FieldFormMetadata {
  grower?: string
  labels?: string[]
  termsets?: { id: string }[]
  termsetIds?: string[]
}

interface FieldFormValues {
  name: string
  description?: string
  owner?: string
  metadata?: FieldFormMetadata
}
const THUMBNAIL_SIZE = 40

interface CreateLandProps {
  onComplete: (field?: FieldModel, isCreatingFieldset?: boolean) => void
  onCancel: () => void
  fieldsetFields?: FieldModel[]
  setSensingDialogOpen?: (open: boolean) => void
  userId?: UserId
}

export const CreateLandImpl = ({
  onComplete,
  onCancel,
  fieldsetFields,
  setSensingDialogOpen,
  userId,
}: CreateLandProps) => {
  const { t } = useTranslation('LandManager/features/CreateLand')
  const [initialPosition, setInitialPosition] = useState<Position>()
  const [featureCollection, setFeatureCollection] = useState<FeatureCollection>()
  const [fieldTouched, setFieldTouched] = useState(false)
  const [acres, setAcres] = useState<number>()
  const [errorMessages, setErrorMessages] = useState<string[]>()
  const [isCreatingFieldset, setIsCreatingFieldset] = useState<boolean>(false)
  const createField = useCreateField()
  const { enqueueSnackbar } = useSnackbar()
  const ipGeoLocation = useIPGeolocation()
  useWindowTitle(t('windowTitle'))

  const { editorOverlaps, majorityCrops, setBaseFeatureCollection, autofixOverlaps } =
    useGeometryEditorStateContext()

  const overlapError = !!editorOverlaps.overlaps?.length
  const noMajorityWarning = majorityCrops && majorityCrops[0] && majorityCrops[0].portion < 0.5

  useEffect(() => {
    if (ipGeoLocation.data) {
      setInitialPosition(ipGeoLocation.data)
    } else {
      setInitialPosition(US_CENTER)
    }
  }, [ipGeoLocation.data])

  const handleSubmit = useCallback(
    async (values: FieldFormValues) => {
      if (!featureCollection?.features[0]) {
        if (fieldsetFields && fieldsetFields.length > 0) {
          onComplete()
        }
        return
      }

      const { name, description, metadata, owner } = values
      const newMetadata: FieldFormMetadata = {}

      // @ts-ignore
      const combinedFeature = featureCollection.features.reduce(union)

      if (metadata) {
        if (metadata.labels) newMetadata.labels = metadata.labels
        if (metadata.grower) newMetadata.grower = metadata.grower
        if (metadata.termsets) {
          const termsetIds = metadata.termsets.map(a => a.id)
          newMetadata.termsetIds = termsetIds
        }
      }

      return createField
        .mutateAsync({
          description,
          feature: combinedFeature,
          metadata: newMetadata,
          owner: owner ? owner?.toString() : undefined,
          name,
        })
        .then(field => {
          enqueueSnackbar(t('success', values), {
            variant: 'success',
          })

          setBaseFeatureCollection(featureCollectionCreator([]))

          onComplete(field, isCreatingFieldset)
        })
        .catch((error: AxiosError<MpxResponseError> | any) => {
          if (
            (error as AxiosError).isAxiosError &&
            error.response?.data?.message === FieldsAPI.ErrorMessages.DUPLICATE_FIELD_NAME
          ) {
            enqueueSnackbar(t('error', { context: 'duplicateName', name }), {
              variant: 'error',
            })
          } else {
            enqueueSnackbar(`${t('error', { context: 'save' })}`, {
              variant: 'error',
            })
          }

          throw error
        })
    },
    [featureCollection, isCreatingFieldset, fieldsetFields]
  )

  const handleUpdate = useCallback((props: { features: FeatureCollection; touched: boolean }) => {
    const { acres, errorMessages } = fieldSizeValidation(props.features)
    setFeatureCollection(props.features)
    setAcres(acres)
    setErrorMessages(errorMessages)
    setFieldTouched(props.touched)
  }, [])

  const initialValues = useMemo(() => ({ name: '', owner: userId }), [userId])

  const alertComponents = useMemo(
    () => (
      <>
        {overlapError && (
          <Alert severity={'error'}>
            <Typography>{t('overlapError')}</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={autofixOverlaps}
              startIcon={<AutoFixHighIcon />}
            >
              {t('autofix')}
            </Button>
          </Alert>
        )}
        {!!acres && acres > 0 && (
          <Alert severity={errorMessages && errorMessages.length > 0 ? 'error' : 'success'}>
            <Typography>{t('acres', { count: acres })}</Typography>
            {errorMessages && errorMessages.map(message => message)}
          </Alert>
        )}
        {noMajorityWarning && (
          <Alert severity={'warning'}>
            <Typography>{t('noMajorityCrop', { year: 2022 })}</Typography>
          </Alert>
        )}
      </>
    ),
    [overlapError, acres, errorMessages?.length, majorityCrops]
  )

  if (ipGeoLocation.isLoading)
    return (
      <Stack sx={{ flex: 1, height: '100%' }} alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    )

  return (
    <Grid sx={{ flex: 1, height: '100%', overflow: 'hidden' }} container>
      <Grid sm={0} md={4} lg={2} item style={{ height: '100%' }}>
        <Paper
          sx={{
            padding: 1,
            borderRadius: 0,
            flex: 1,
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            maxHeight: '100vh',
          }}
        >
          <Stack spacing={2} style={{ flex: 1 }} divider={<Divider />}>
            <Typography variant="h5">{t('createField')}</Typography>
            <FieldEditorForm
              initialValues={initialValues}
              onSubmit={handleSubmit}
              onCancel={onCancel}
              fieldTouched={fieldTouched}
              setIsCreatingFieldset={setIsCreatingFieldset}
              isCreatingFieldset={isCreatingFieldset}
              fieldset={fieldsetFields}
              setSensingDialogOpen={setSensingDialogOpen}
              alertComponents={alertComponents}
            />
            {fieldsetFields && (
              <Box sx={{ flex: '1 1 auto', maxWidth: 204, overflow: 'auto' }}>
                <Stack spacing={0.5}>
                  <Typography variant="h6">{t('creatingFields')}</Typography>
                  {fieldsetFields.map((field: FieldModel) => (
                    <Card key={field.resourceId} sx={{ padding: 1 }} elevation={0}>
                      <Stack direction="row" spacing={2} alignItems="left" justifyContent="left">
                        <Box style={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}>
                          <LandThumbnail
                            landProfile={field}
                            width={THUMBNAIL_SIZE}
                            height={THUMBNAIL_SIZE}
                            skin={LandThumbnailSkin.SMALL}
                          />
                        </Box>

                        <Stack sx={{ width: 132 }}>
                          <LineLimitTypography lineLimit={1}>{field.name}</LineLimitTypography>
                          <LineLimitTypography lineLimit={1} variant="caption">
                            {field.ownerLabel}
                          </LineLimitTypography>
                        </Stack>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>
      </Grid>
      <Grid sm={12} md={8} lg={10} sx={{ height: '100%', display: 'flex' }} item>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <LayerProvider>
            <GeometryEditor onChange={handleUpdate} initialPosition={initialPosition} />
          </LayerProvider>
        </Box>
      </Grid>
    </Grid>
  )
}

export const CreateLand = (props: CreateLandProps) => (
  <GeometryEditorContext>
    <CreateLandImpl {...props} />
  </GeometryEditorContext>
)
